import React, { useState } from "react";
import {
  Facebook,
  Twitter,
  Instagram,
  Mail,
  Phone,
  X,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";import { useTranslation } from 'react-i18next';


const policies = {
  privacy: {
    title: "Privacy Policy",
    content: `
DigiQin respects your privacy and is committed to protecting your data.
We collect only the information necessary to provide our software services,
improve performance, and support our users.

Your business data always remains yours. We do not sell or misuse personal
information. By using DigiQin, you agree to our Privacy Policy.
    `,
  },
  terms: {
    title: "Terms & Conditions",
    content: `
By accessing or using DigiQin, you agree to comply with our Terms & Conditions.
Unauthorized use or violation of laws may result in suspension or termination.
    `,
  },
  refund: {
    title: "Refund & Cancellation Policy",
    content: `
DigiQin subscriptions and services are non-refundable once activated.
Refunds, if any, are subject to DigiQin’s Refund Policy.
    `,
  },
};

const Footer = () => {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null);

  return (
    <>
      {/* FOOTER */}
      <footer className="relative bg-white overflow-hidden">

        <div className="relative container mx-auto px-6 py-20 text-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

            {/* PRODUCTS */}
            <div>
              <h3 className="footer-title">Products</h3>
              <ul className="footer-list">
                <li>Logix-DQ</li>
                <li>IMS-DQ</li>
                <li>Web & Digital Marketing</li>
              </ul>
            </div>

            {/* RESOURCES */}
            <div>
              <h3 className="footer-title">Resources</h3>
              <ul className="footer-list">
                <li>Documentation</li>
                <li>Videos</li>
              </ul>
            </div>

            {/* LEGAL */}
            <div>
              <h3 className="footer-title">Legal & Compliance</h3>
              <ul className="footer-list">
                <li onClick={() => setActiveModal("privacy")}>
                  Privacy Policy
                </li>
                <li onClick={() => setActiveModal("terms")}>
                  Terms & Conditions
                </li>
                <li onClick={() => setActiveModal("refund")}>
                  Refund & Cancellation Policy
                </li>
              </ul>
            </div>

            {/* CONTACT */}
            <div>
              <h3 className="footer-title">Contact</h3>
              <div className="space-y-4 text-sm">
                <div className="footer-contact">
                  <Mail className="w-4 h-4 text-purple-600" />
                  info@digiqin.com
                </div>
                <div className="footer-contact">
                  <Phone className="w-4 h-4 text-purple-600" />
                  98925 75520
                </div>

                <div className="flex gap-4 pt-4">
                  {[Facebook, Twitter, Instagram].map((Icon, i) => (
                    <div key={i} className="footer-social">
                      <Icon className="w-4 h-4" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* ✅ BOTTOM BAR – LEFT / RIGHT ALIGNED */}
        <div className="border-t border-purple-100 bg-purple-50 py-6">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-gray-600">
              
              {/* LEFT */}
              <div>
                © {new Date().getFullYear()}{" "}
                <span className="font-semibold text-purple-700">DigiQin</span>. All rights
                reserved.
              </div>

              {/* RIGHT */}
              <div>
                Designed by{" "}
                <span className="font-semibold text-purple-600">
                  DigiQin Design Team
                </span>
              </div>

            </div>
          </div>
        </div>

      </footer>

      {/* MODAL */}
      {activeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-purple-900/20 backdrop-blur-sm"
            onClick={() => setActiveModal(null)}
          />

          <div className="relative z-10 w-[92%] max-w-2xl rounded-2xl bg-white p-8 md:p-10 shadow-xl animate-fadeIn">
            <button
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-purple-600"
            >
              <X />
            </button>

            <h2 className="text-2xl font-semibold mb-2 bg-gradient-to-r from-purple-700 via-purple-500 to-indigo-600 bg-clip-text text-transparent">
              {policies[activeModal].title}
            </h2>

            <div className="h-[2px] w-16 bg-gradient-to-r from-purple-600 to-indigo-500 rounded-full mb-6" />

            <p className="text-gray-700 leading-relaxed whitespace-pre-line mb-8 text-lg">
              {policies[activeModal].content}
            </p>

            <button
              onClick={() => {
                setActiveModal(null);
                if (activeModal === "privacy")
                  navigate("/privacy-policy");
                if (activeModal === "terms")
                  navigate("/terms-conditions");
                if (activeModal === "refund")
                  navigate("/refund-cancellation-policy");
              }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 hover:shadow-lg transition-all"
            >
              Read More
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* STYLES */}
      <style>{`
        .footer-title {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: #7e22ce;
          margin-bottom: 1.5rem;
        }

        .footer-list li {
          font-size: 0.9rem;
          margin-bottom: 0.75rem;
          cursor: pointer;
          transition: color 0.25s ease;
        }

        .footer-list li:hover {
          color: #6d28d9;
        }

        .footer-contact {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .footer-social {
          width: 36px;
          height: 36px;
          border-radius: 9999px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f3ff;
          color: #7c3aed;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .footer-social:hover {
          background: #7c3aed;
          color: #fff;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.35s ease;
        }
      `}</style>
    </>
  );
};

export default Footer;
